import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth/auth-context';
import { Spinner } from '@/components/ui/Spinner';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * Wraps protected routes. Redirects to Keycloak if not authenticated.
 * Stores the original path so the OIDC callback can return the user there.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void login(location.pathname + location.search);
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search, login]);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-sm text-gray-500">Verifying session…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-sm text-gray-500">Redirecting to sign in…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
